<template>
  <a-modal
    v-if="visible"
    :visible="visible"
    @cancel="closeModal"
    title="Create New Profile"
    width="400px"
    :destroy-on-close="true"
  >
    <div v-if="loading" class="loading-spin">
      <a-spin size="large" />
    </div>
    <div v-else>
      <a-modal title="You can disable previous associate" v-model="openedDisablePreviousAssociateModal" :footer="null">
        <div class="flex flex-column gap-4">
          <div>
            <p>You are about to set a new associate with a different pay type or role.</p>
            <p>You have the option to disable the previous associate profile if it is no longer needed.</p>
          </div>
          <div class="flex gap-2 justify-content-end">
            <a-button @click="cloneAssociate(false)">Leave The Account</a-button>
            <a-button @click="cloneAssociate(true)" type="danger">Disable Previous Associate</a-button>
          </div>
        </div>
      </a-modal>

      <a-form :form="form" :label-col="{ span: 12 }" label-align="left" layout="vertical" :wrapper-col="{ span: 12 }">
        <div class="flex flex-column gap-3">
          <div class="flex flex-column gap-2">
            <h3 class="m-0">Personal Info</h3>

            <a-descriptions :column="1" size="small" class="col-fixed p-0">
              <a-descriptions-item label="Full Name">
                {{ associate.first_name }} {{ associate.last_name }}
              </a-descriptions-item>
              <a-descriptions-item label="Email">
                {{ associate.email }}
              </a-descriptions-item>
              <a-descriptions-item label="Phone">
                {{ associate.phone }}
              </a-descriptions-item>
              <a-descriptions-item label="Birth Date">
                {{ birthDate }}
              </a-descriptions-item>
              <a-descriptions-item label="Address">
                {{ address }}
              </a-descriptions-item>
            </a-descriptions>
          </div>

          <div class="flex flex-column">
            <h3 class="m-0 pb-2">Company Info</h3>
            <a-form-item required label="Associate Number">
              <a-input
                v-decorator="[
                  'associateNumber',
                  {
                    rules: [
                      {
                        required: true,
                        message: associateNumberSuggestionMsg
                      },
                      {
                        len: 6,
                        message: 'The associate number must be 6 characters.'
                      },
                      { validator: associateNumberValidator }
                    ]
                  }
                ]"
                placeholder="Enter associate number"
              />
            </a-form-item>
            <a-form-item required label="Position">
              <custom-select
                v-decorator="[
                  'projectRole',
                  {
                    rules: [
                      {
                        required: true,
                        message: 'Please select a position!'
                      }
                    ]
                  }
                ]"
                :multiple="false"
                :allow-clear="false"
                placeholder="Position"
                :options="projectRoles"
                name="name"
                optionKey="id"
              />
            </a-form-item>
          </div>

          <div class="flex flex-column">
            <h3 class="m-0 pb-2">Payment Info</h3>

            <a-form-item label="Payment Type" required>
              <a-select
                v-decorator="[
                  'paymentType',
                  {
                    rules: [{ required: true }],
                    message: 'Payment Type is required'
                  }
                ]"
                placeholder="Select PT"
                @change="handlePaymentTypeChange"
              >
                <a-select-option v-for="type in paymentTypes" :key="type.id" :value="type.id"
                  >{{ type.name }}
                </a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item label="Payment Status">
              <a-select
                v-decorator="[
                  'paymentStatus',
                  {
                    rules: [{ required: true }],
                    initialValue: undefined
                  }
                ]"
                placeholder="Payment Status"
              >
                <a-select-option v-for="status in paymentStatuses" :key="status.id" :value="status.id"
                  >{{ status.label }}
                </a-select-option>
              </a-select>
            </a-form-item>

            <template v-if="isEmployeePaymentTypeSelected">
              <a-form-item label="Payment Method">
                <a-select
                  v-decorator="[
                    'paymentMethod',
                    {
                      rules: [{ required: false }]
                    }
                  ]"
                  placeholder="Payment Method"
                >
                  <a-select-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :value="method.id"
                    :disabled="method.isDisabled"
                    >{{ method.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>

              <a-form-item label="Hourly Rate, $">
                <a-input-number
                  v-decorator="['hourly_rate', { rules: [{ required: false }], initialValue: undefined }]"
                  placeholder="
                    Hourly Rate
                  "
                  :min="0"
                />
              </a-form-item>
            </template>

            <template v-if="isContractorPaymentTypeSelected">
              <a-form-item required label="Legal Entity">
                <custom-select
                  v-decorator="[
                    'legalEntity',
                    {
                      rules: [
                        {
                          required: isContractorPaymentTypeSelected,
                          message: 'Select an Legal Entity'
                        }
                      ]
                    }
                  ]"
                  placeholder="Legal Entity"
                  :options="agencies"
                  name="name"
                  optionKey="id"
                />
              </a-form-item>

              <a-form-item label="Role">
                <div class="flex flex-column gap-2">
                  <a-radio-group
                    button-style="solid"
                    v-decorator="['is_legal_entity_admin', { rules: [{ required: false }], initialValue: false }]"
                  >
                    <a-radio-button :value="true"> Admin </a-radio-button>
                    <a-radio-button :value="false"> Associate </a-radio-button> </a-radio-group
                  ><a-button v-if="canCreateLegalEntity" @click="handleOpenLegalEntity" type="primary" class="w-full"
                    >Create New</a-button
                  >
                </div>
              </a-form-item>

              <a-form-item label="Hourly Rate, $">
                <a-input-number
                  v-decorator="['hourly_rate', { rules: [{ required: false }], initialValue: 0 }]"
                  placeholder="Enter rate"
                  :min="0"
                />
              </a-form-item>
            </template>

            <a-form-item label="Allow Instant Payment">
              <a-checkbox
                v-decorator="[
                  'allowInstantPayment',
                  {
                    rules: [
                      {
                        required: false,
                        type: 'boolean',
                        initialValue: true
                      }
                    ]
                  }
                ]"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <a-button
          type="primary"
          :loading="loadingForm"
          :disabled="enabledCloneButton"
          @click="
            isStaff || associate.work_status === 'disabled' ? cloneAssociate(true) : toggleDisableSuggestionModal()
          "
        >
          Create
        </a-button>
      </div>
    </template></a-modal
  >
</template>

<script>
import {
  Form,
  Input,
  InputNumber,
  Select,
  notification,
  Button,
  Checkbox,
  Modal,
  Spin,
  Radio,
  Descriptions
} from 'ant-design-vue'
import Util from '@/util'
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment-timezone'
import constData from '@/helpers/const-data'
import CustomSelect from '@/components/ui/custom-select.vue'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapActions: legalEntityActions } = createNamespacedHelpers('legalEntity')

const PaymentType = Object.freeze({
  EMPLOYEE: 3,
  CONTRACTOR: 6,
  VOLUNTEER: 5
})

const PaymentStatus = Object.freeze({
  FULL_TIME: 'full_time',
  PART_TIME: 'part_time',
  ON_CALL: 'on_call'
})

const PaymentMethod = Object.freeze({
  HOURLY: 0,
  SALARY: 1,
  MARKETPLACE_RATE: 2
})

export default {
  components: {
    'a-form': Form,
    'a-form-item': Form.Item,
    'a-input': Input,
    'a-input-number': InputNumber,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-modal': Modal,
    'a-spin': Spin,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'custom-select': CustomSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associateNumber: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      form: undefined,
      hourly_rate: undefined,
      paymentTypes: [
        { id: PaymentType.EMPLOYEE, name: 'Employee' },
        { id: PaymentType.CONTRACTOR, name: 'Contractor' },
        { id: PaymentType.VOLUNTEER, name: 'Volunteer' }
      ],
      paymentStatuses: [
        { id: PaymentStatus.FULL_TIME, label: 'Full Time' },
        { id: PaymentStatus.PART_TIME, label: 'Part Time' },
        { id: PaymentStatus.ON_CALL, label: 'On Call' }
      ],

      isEmployeePaymentTypeSelected: false,
      isContractorPaymentTypeSelected: false,

      associateNumberSuggestionMsg: 'Please enter associate number of new associate',

      associateNumberErrorMsg: 'Associate number must be exactly 6 digits.',

      loadingForm: false,

      enabledCloneButton: false,

      openedDisablePreviousAssociateModal: false,

      loading: false,

      statesList: constData.usStates,
      countriesList: constData.countries,

      visibleAgencyModal: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },
    paymentMethods() {
      return [
        { id: PaymentMethod.HOURLY, label: 'Hourly', isDisabled: false },
        { id: PaymentMethod.SALARY, label: 'Salary', isDisabled: false },
        {
          id: PaymentMethod.MARKETPLACE_RATE,
          label: 'Marketplace Rate',
          isDisabled: !(
            this.isContractorPaymentTypeSelected && this.$store.state.applicationState.currentProject.staffing
          )
        }
      ]
    },

    birthDate() {
      return this.associate.birth_date
        ? moment(this.associate.birth_date).format(this.$store.state.applicationState.dateFormat)
        : '-'
    },

    address() {
      const { address } = this.associate

      if (!address) {
        return ' - '
      }

      const country = this.countriesList.find(country => country.code === address.country)

      const state = this.statesList.find(state => state.code === address.state)

      const parts = [
        address?.address_line1,
        address?.address_line2,
        address?.zipcode,
        address?.city,
        state?.name,
        country?.name
      ]

      return parts.filter(part => part).join(', ')
    },

    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    serverDateFormat() {
      return this.$store.state.applicationState.serverDateFormat
    },
    projectRoles() {
      return this.$store.state.applicationState.projectRoles
    },
    agencies() {
      return this.$store.state.applicationState.projectLegalEntities
    },
    canCreateLegalEntity() {
      return !this.getFieldValue('legalEntity')
    }
  },
  methods: {
    ...profileActions(['getAssociate', 'checkAvailableAssociateNumber', 'postCloneAssociate']),
    ...modalActions(['openModal']),
    ...legalEntityActions(['getLegalEntity']),

    showNotification(type, message, description) {
      notification[type]({
        message: message,
        description: description
      })
    },

    handleOpenLegalEntity() {
      if (!this.canCreateLegalEntity) return

      this.openModal({
        type: 'legalEntity',
        data: {
          visible: true,
          initialValues: {
            type: 'independent_contractor',
            address: this.associate.address,
            name: `${this.associate.first_name} ${this.associate.last_name}`,
            tax_id: (this.associate.social_security_number ?? '').replace(/\D/g, '')
          },
          add: async legal_entity => {
            this.form.setFieldsValue({ legalEntity: legal_entity.id })
          },
          showCreateAssociate: false
        }
      })
    },

    closeModal() {
      this.$emit('update:visible', false)
    },

    getFieldValue(name) {
      return this.form ? this.form.getFieldValue(name) : undefined
    },

    async fetchAssociate() {
      this.loading = true

      const payload = {
        associate_number: this.associateNumber
      }

      if (!this.associateNumber) {
        return
      }

      try {
        await this.getAssociate(payload)
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async associateNumberValidator(rule, value, callback) {
      if (value.length !== 6) {
        return
      }

      try {
        const result = await this.checkAvailableAssociateNumber({
          associate_number: value
        })

        if (result.owner) {
          callback(
            new Error(
              `This associate number is already occupied by another user in this company. You can use available associate number: ${result.available_number} `
            )
          )
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    handlePaymentTypeChange(value) {
      switch (value) {
        case PaymentType.EMPLOYEE:
          this.isEmployeePaymentTypeSelected = true
          this.isContractorPaymentTypeSelected = false
          break
        case PaymentType.CONTRACTOR:
          this.isEmployeePaymentTypeSelected = false
          this.isContractorPaymentTypeSelected = true
          break
        default:
          this.isEmployeePaymentTypeSelected = false
          this.isContractorPaymentTypeSelected = false
      }
    },

    confirmCloneRecords() {
      return new Promise(resolve => {
        Modal.confirm({
          title: 'Transfer',
          content:
            'Do you want to clone all associated records (completed sequences, signed documents, attached documents, written warnings, etc.)? If not, you can transfer them later using the provided option.',
          okText: 'Yes',
          cancelText: 'No',
          onOk() {
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      })
    },

    confirmCreateInStuff() {
      return new Promise(resolve => {
        Modal.confirm({
          title: 'New Profile Instead of the Old One',
          content:
            'Your current profile will be disabled! You can create a new profile to work with a different payment configuration, BUT only one profile can be active in the Staff Company at a time. Please keep this in mind!',
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk() {
            resolve(true)
          },
          onCancel() {
            resolve(false)
          }
        })
      })
    },

    cloneAssociate(withDisablePrevious) {
      this.form.validateFields(async (err, values) => {
        this.openedDisablePreviousAssociateModal = false

        if (err) {
          this.showIncorrectFormError()
          return
        }

        if (this.isStaff) {
          const isCreateConfirm = await this.confirmCreateInStuff()

          if (!isCreateConfirm) return
        }

        const isCloneRecords = await this.confirmCloneRecords()

        this.loadingForm = true

        const newAssociateNumber = values.associateNumber

        try {
          const legalEntity = this.isContractorPaymentTypeSelected
            ? await this.getLegalEntity({ legal_entity_id: values.legalEntity })
            : undefined

          const associateInformation = {
            associate_number: this.associateNumber,
            automatic_completing_actions: this.associate.automatic_completing_actions,
            schedule_type: this.associate.schedule_type,
            role: { id: values.projectRole },
            allow_instant_payment: values.allowInstantPayment ?? false,
            payment_type: Object.keys(PaymentType).find(key => PaymentType[key] === values.paymentType),
            pay_status: values.paymentStatus,
            pay_method: values.paymentMethod
              ? Object.keys(PaymentMethod)
                  .find(key => PaymentMethod[key] === values.paymentMethod)
                  .toLowerCase()
              : 'hourly',
            hourly_rate: values.hourly_rate ? Util.convertMoneyToDb(values.hourly_rate) : 0,
            legal_entity: this.isContractorPaymentTypeSelected ? { ...legalEntity } : undefined,
            is_verified: true
          }

          if (!associateInformation.legal_entity && this.isContractorPaymentTypeSelected) {
            let message

            if (!values.hourly_rate) {
              message = `Hourly rate for Legal Entity "${this.legalEntity.name}" is not specified!`
            }

            this.showIncorrectFormError(message)
            this.loadingForm = false

            return
          }

          const payload = {
            new_associate_number: newAssociateNumber,
            associate: associateInformation,
            disable_previous_associate: withDisablePrevious,
            is_legal_entity_admin: values.is_legal_entity_admin,
            clone_records: isCloneRecords
          }

          await this.postCloneAssociate(payload)

          notification.success({
            message: 'Success',
            description: 'New profile successfully created'
          })

          this.$emit('close', newAssociateNumber)
          this.closeModal()
        } catch (error) {
          notification.error({
            message: 'Error',
            description: error
          })
        } finally {
          this.loadingForm = false
        }
      })
    },
    showIncorrectFormError(msg) {
      notification.warning({
        message: 'Incorrect form',
        description: msg ?? 'Some fields are not filled in or are filled in incorrectly. Please check the fields'
      })
    },

    toggleDisableSuggestionModal() {
      this.openedDisablePreviousAssociateModal = !this.openedDisablePreviousAssociateModal
    }
  },
  beforeMount() {
    this.form = this.$form.createForm(this, { name: 'create_user_form' })
  },
  mounted() {
    this.fetchAssociate()
  }
}
</script>
